import { CallRecordingPlayer } from '@/components/CallRecordingPlayer';
import { HoverDisplay } from '@/components/HoverDisplay';
import { PriorityIcon } from '@/components/PriorityIcon';
import { RestrictedContentManager } from '@/pages/PatientTimelinePage/RestrictedContentManager';
import { getContactLabel } from '@/services/contact-point';
import { changeResourcePriority } from '@/services/priority-management';
import { CommunicationDirection } from '@bariendo/lib/constants';
import {
  getVoiceCallDisplayStatus,
  VOICE_CALL_DISPLAY_BADGE,
  VoiceCallDisplayStatus,
} from '@bariendo/lib/voice-call-outcome';
import { normalizePhoneNumber } from '@bariendo/lib/phone-validations';
import { ActionIcon, Anchor, Badge, Box, Flex, Grid, Group, Menu, Stack, Text, Tooltip } from '@mantine/core';
import { capitalize, getReferenceString } from '@medplum/core';
import { Communication, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  IconArrowUpRight,
  IconClock,
  IconFilePlus,
  IconLink,
  IconPhoneOff,
  IconPhoneX,
  IconUserShare,
} from '@tabler/icons-react';
import { usePostHog } from 'posthog-js/react';
import { ReactElement, useMemo, useState } from 'react';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { buildPhoneCardDetails } from '@/services/Timeline/phoneCardDetails';
import { getPatientPhones } from '@/pages/PatientTimelinePage/services/timelinePatient';
import { getOrganizationPhones } from '@/pages/PartnersPage/PartnerTimeline/services/timelineOrganization';
import { TimelineNotes } from './TimelineNotes';
import { TimelineNoteInput } from './TimelineNoteInput';
import {
  TimelineNote,
  TimelineVoiceCommunication,
  TwilioCallError,
  VirtualReceptionistData,
  VirtualReceptionistProvidersTried,
} from '../types';
import { AssigneeAvatar } from './AssigneeAvatar';
import { CallParticipantAvatar } from './CallParticipantAvatar';
import { CARD_MENU_ICON_COLOR, CardMenu } from './CardMenu';
import { DateTimeTooltip } from './DateTimeTooltip';
import { HighlightableItem } from './HighlightableItem';
import { TimelineCard } from './TimelineCard';
import { callRecordingPlayHandler, callRecordingPauseHandler } from './callRecordingTracking';

function formatDuration(seconds?: number): string {
  if (!seconds) return '';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function getDisplayStatusBadgeIcon(displayStatus: VoiceCallDisplayStatus) {
  switch (displayStatus) {
    case 'missed-call':
      return <IconPhoneOff size={12} />;
    case 'no-answer':
    case 'failed':
      return <IconPhoneX size={12} />;
    default:
      return undefined;
  }
}

function getStatusBadge(item: TimelineVoiceCommunication) {
  const displayStatus = getVoiceCallDisplayStatus({
    callOutcome: item.callOutcome,
    direction: item.direction,
    fhirStatus: item.status,
  });

  if (!displayStatus) {
    return (
      <Badge size="sm" color="gray" variant="light">
        unknown
      </Badge>
    );
  }

  const { label, color } = VOICE_CALL_DISPLAY_BADGE[displayStatus];
  const icon = getDisplayStatusBadgeIcon(displayStatus);

  return (
    <Badge size="sm" color={color} variant="light" leftSection={icon}>
      {label}
    </Badge>
  );
}

interface PhoneCallHeaderProps {
  item: TimelineVoiceCommunication;
  /** Every action on this card lives in the header menu — the card has no
   * footer cluster of its own any more. */
  onAddNote: () => void;
  isNoteInputOpen: boolean;
  onCopyLink: () => void;
  onChangePriority: (priority: 'urgent' | undefined) => void;
  canMarkUrgent: boolean;
  canRemoveUrgent: boolean;
}

function PhoneCallHeader({
  item,
  onAddNote,
  isNoteInputOpen,
  onCopyLink,
  onChangePriority,
  canMarkUrgent,
  canRemoveUrgent,
}: PhoneCallHeaderProps) {
  const { patientContext, partnerContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const organization = partnerContext?.organization;
  const isPartnerContext = !!partnerContext;

  const patientPhones = useMemo(() => (patient ? getPatientPhones(patient) : []), [patient]);
  const organizationPhones = useMemo(() => getOrganizationPhones(organization), [organization]);

  const phones = isPartnerContext ? organizationPhones : patientPhones;

  // The Reassign icon and the avatar open the SAME picker.
  const [reassignOpen, setReassignOpen] = useState(false);

  const isPatientCall = item.direction === CommunicationDirection.INCOMING;

  const baseTitle = isPatientCall ? `Incoming call` : `Outgoing call`;

  const title = <Text variant="header">{baseTitle}</Text>;

  const statusBadge = getStatusBadge(item);
  const patientPhoneLabel = getContactLabel(
    phones.find((phone) => normalizePhoneNumber(phone.value ?? '') === item.patientNumber)
  );

  const details = useMemo(
    () => [
      ...buildPhoneCardDetails({
        patientNumber: item.patientNumber,
        orbitNumbers: [item.orbitNumber],
        patientLabel: patientPhoneLabel,
      }),
      // Only for a call that connected — a missed call has no duration.
      ...(item.duration ? [{ label: 'Call duration', value: formatDuration(item.duration) }] : []),
    ],
    [item.patientNumber, item.orbitNumber, patientPhoneLabel, item.duration]
  );

  // Unchanged from main. Keying off the assignment Task instead would show a
  // blank "Unassigned" circle, since a Task survives being unassigned.
  const showAssigneeSlot = isPatientCall && Boolean(item.virtualReceptionistData);

  return (
    <Group justify="space-between" align="center" wrap="nowrap">
      <Flex gap="xs" align="center" style={{ minWidth: 0 }}>
        {title}
        {statusBadge}
        {item.twilioErrors && item.twilioErrors.length > 0 && (
          <Badge size="sm" color="red" variant="filled">
            {item.twilioErrors.length === 1 ? 'Error' : `${item.twilioErrors.length} Errors`}
          </Badge>
        )}
      </Flex>
      {/* Canon header cluster: actions -> overflow -> avatar -> time. */}
      <Group gap="xs" align="center" style={{ flexShrink: 0 }}>
        {showAssigneeSlot && (
          <Tooltip label="Reassign">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Reassign"
              onClick={() => setReassignOpen(true)}
            >
              <IconUserShare size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        <CardMenu aria-label="More call actions" details={details}>
          <Menu.Item
            leftSection={<IconFilePlus size={18} color={CARD_MENU_ICON_COLOR} />}
            onClick={onAddNote}
            disabled={isNoteInputOpen}
          >
            Add sticky note
          </Menu.Item>
          <Menu.Item leftSection={<IconLink size={18} color={CARD_MENU_ICON_COLOR} />} onClick={onCopyLink}>
            Copy link
          </Menu.Item>
          {canMarkUrgent && (
            <Menu.Item
              leftSection={<IconClock size={18} color={CARD_MENU_ICON_COLOR} />}
              onClick={() => onChangePriority('urgent')}
            >
              Mark as urgent
            </Menu.Item>
          )}
          {canRemoveUrgent && (
            <Menu.Item
              leftSection={<IconClock size={18} color={CARD_MENU_ICON_COLOR} />}
              onClick={() => onChangePriority(undefined)}
            >
              Mark as not urgent
            </Menu.Item>
          )}
        </CardMenu>
        {showAssigneeSlot ? (
          <AssigneeAvatar communication={item.resource} opened={reassignOpen} onOpenedChange={setReassignOpen} />
        ) : (
          <CallParticipantAvatar item={item} />
        )}
        <DateTimeTooltip date={item.timestamp} />
        <PriorityIcon priority={item.priorityStatus} />
      </Group>
    </Group>
  );
}

function RecordingUnavailableMessage({ message }: { message: string }) {
  return (
    <Box
      p="sm"
      style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
      }}
    >
      <Text size="sm" c="dimmed">
        {message}
      </Text>
    </Box>
  );
}

function VirtualReceptionistDataTable({ data }: { data: VirtualReceptionistData }) {
  const knownDisplayProps: (keyof VirtualReceptionistData)[] = [
    // shown
    'caller_name',
    'call_type',
    'requested_representative_name',
    'question',
    'message',
    'full_messsage_link',
    // hidden
    'lead_id',
    'activity_at',
    'date_created',
    'caller_phone_number',
    'answerconnect_message_id',
  ];
  const extraProps = Object.keys(data).filter(
    (key) => !knownDisplayProps.includes(key as keyof VirtualReceptionistData)
  );
  return (
    <Box>
      <Text fw={600} mb="xs">
        Forwarded to answering service
      </Text>
      <Grid gutter="xs">
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text fw={600}>Caller name</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Text>{data.caller_name || 'N/A'}</Text>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text fw={600}>Call type</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Text>{data.call_type || 'N/A'}</Text>
        </Grid.Col>

        {data.requested_representative_name && (
          <>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Text fw={600}>Requested representative</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <Text>{data.requested_representative_name || 'N/A'}</Text>
            </Grid.Col>
          </>
        )}

        {data.question && (
          <>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Text fw={600}>Question</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <Text>{data.question || 'N/A'}</Text>
            </Grid.Col>
          </>
        )}

        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text fw={600}>Message</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
            {data.message || 'N/A'}
          </Text>
        </Grid.Col>

        {data.full_messsage_link && (
          <>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Text fw={600}>Full message link</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <a href={data.full_messsage_link} target="_blank" rel="noopener noreferrer">
                {data.full_messsage_link || 'N/A'}
              </a>
            </Grid.Col>
          </>
        )}
        {extraProps.length > 0
          ? extraProps.map((prop) => (
              <>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text fw={600}>
                    {prop
                      .split('_')
                      .map((word) => capitalize(word))
                      .join(' ')}
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <Text>{data[prop as keyof VirtualReceptionistData] || 'N/A'}</Text>
                </Grid.Col>
              </>
            ))
          : null}
      </Grid>
    </Box>
  );
}

function VirtualReceptionistProvidersTriedHistoryData({ data = [] }: { data?: VirtualReceptionistProvidersTried[] }) {
  if (!data.length) {
    return;
  }
  return (
    <Box>
      <Grid gutter="xs">
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Text fw={600}>Providers tried before forwarding</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <ol style={{ margin: 0 }}>
            {data.map((provider) => (
              <li>
                <Text>{provider.valueReference?.display}</Text>
              </li>
            ))}
          </ol>
        </Grid.Col>
      </Grid>
    </Box>
  );
}

export function PhoneCallTimelineItem({
  item,
  notes = [],
  highlightRef,
  copyDirectLink,
  hasActiveFilters,
  handleNavigateToItem,
  searchTerm,
}: {
  item: TimelineVoiceCommunication;
  notes?: TimelineNote[];
  highlightRef?: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  hasActiveFilters: boolean;
  handleNavigateToItem: (itemReference: string) => void;
  searchTerm?: string;
}) {
  const [showNoteInput, setShowNoteInput] = useState(false);
  const medplum = useMedplum();
  const posthog = usePostHog();

  const { patientContext, partnerContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const organization = partnerContext?.organization;
  const isPartnerContext = !!partnerContext;

  const handleAddNote = () => {
    setShowNoteInput(true);
  };

  const hideNoteInput = () => {
    setShowNoteInput(false);
  };

  const handleChangePriority = async (priority: 'urgent' | undefined) => {
    await changeResourcePriority(medplum, item.reference, priority);
  };

  const showMarkAsUrgent = () =>
    item.isAssignable && item.priorityStatus !== 'urgent' && item.priorityStatus !== 'overdue';

  const showRemoveUrgent = () =>
    item.isAssignable && (item.priorityStatus === 'urgent' || item.priorityStatus === 'overdue');

  const recordingAttachments = item.recordingAttachments ?? [];

  const handlePlay = callRecordingPlayHandler({
    item,
    posthog,
    organizationId: isPartnerContext && organization?.id ? organization.id : undefined,
    patientId: patient?.id,
  });

  const handlePause = callRecordingPauseHandler({
    item,
    posthog,
    organizationId: isPartnerContext && organization?.id ? organization.id : undefined,
    patientId: patient?.id,
  });

  return (
    <TimelineCard
      header={
        <PhoneCallHeader
          item={item}
          onAddNote={handleAddNote}
          isNoteInputOpen={showNoteInput}
          onCopyLink={() => copyDirectLink(getReferenceString(item.reference)!)}
          onChangePriority={(priority) => {
            handleChangePriority(priority).catch(() => undefined);
          }}
          canMarkUrgent={Boolean(showMarkAsUrgent())}
          canRemoveUrgent={Boolean(showRemoveUrgent())}
        />
      }
    >
      <PhoneCallErrors errors={item.twilioErrors} />
      <Group justify="space-between" align="flex-start">
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          <RestrictedContentManager isRestricted={item.hiddenPayload}>
            {item.virtualReceptionistData && (
              <>
                <VirtualReceptionistDataTable data={item.virtualReceptionistData} />
                <VirtualReceptionistProvidersTriedHistoryData data={item.virtualReceptionistProvidersTriedHistory} />
              </>
            )}
            <CallRecordingPlayer
              attachments={recordingAttachments}
              resetKey={item.reference.reference}
              onPlay={handlePlay}
              onPause={handlePause}
            >
              {(player: ReactElement) => (
                <HoverDisplay>
                  <HighlightableItem itemRef={getReferenceString(item.reference)!} highlightRef={highlightRef}>
                    {player}
                  </HighlightableItem>
                </HoverDisplay>
              )}
            </CallRecordingPlayer>
            {recordingAttachments.length === 0 && item.recordingUnavailableMessage && !item.virtualReceptionistData && (
              <RecordingUnavailableMessage message={item.recordingUnavailableMessage} />
            )}
          </RestrictedContentManager>

          {/* Timeline Note Input */}
          {showNoteInput && (
            <TimelineNoteInput
              timelineItemReference={item.reference as Reference<Communication>}
              onCancel={hideNoteInput}
              onSave={hideNoteInput}
            />
          )}

          {/* Timeline Notes Display */}
          {notes && notes.length > 0 && (
            <TimelineNotes
              notes={notes}
              highlightRef={highlightRef}
              copyDirectLink={copyDirectLink}
              hasActiveFilters={hasActiveFilters}
              handleNavigateToItem={handleNavigateToItem}
              searchTerm={searchTerm}
            />
          )}
        </Stack>

        <Stack gap="xs" align="flex-start" style={{ flexShrink: 0 }}>
          {/* The kebab moved to the header; only the filtered-view jump is
              still anchored to the body. */}
          {hasActiveFilters && (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => handleNavigateToItem(getReferenceString(item.reference)!)}
              title="See full context"
            >
              <IconArrowUpRight size={16} />
            </ActionIcon>
          )}
        </Stack>
      </Group>
    </TimelineCard>
  );
}

function PhoneCallErrors({ errors }: { errors?: TwilioCallError[] }) {
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <Box>
      <Text size="sm" variant="filled" style={{ fontWeight: '600' }}>
        Error completing this phone call.
      </Text>
      {showErrorDetails && (
        <Stack gap="xs" style={{ marginTop: '8px' }}>
          {errors.map((error, index) => (
            <Box key={index}>
              <Text style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                Code: {error.errorCode}
                {error.errorMessage && ` - ${error.errorMessage}`}
              </Text>
            </Box>
          ))}
        </Stack>
      )}
      <Anchor
        component="button"
        onClick={() => setShowErrorDetails(!showErrorDetails)}
        style={{ fontSize: '14px', marginTop: '4px' }}
      >
        {showErrorDetails ? 'Show Less...' : 'See full Error'}
      </Anchor>
    </Box>
  );
}
